import traverse from "traverse";

// from https://github.com/madprime/python-gedcom/blob/master/gedcom/__init__.py
// * Level must start with nonnegative int, no leading zeros.
// * Pointer optional, if it exists it must be flanked by '@'
// * Tag must be alphanumeric string
// * Value optional, consists of anything after a space to end of line
//   End of line defined by \n or \r
const lineRe = /\s*(0|[1-9]+[0-9]*) (@[^@]+@ |)([A-Za-z0-9_]+)( [^\n\r]*|)/;

function hasTag(val) {
  return node => node.tag === val;
}

export function d3ize(tree) {
  var peopleNodes = tree.filter(hasTag("INDI")).map(toNode);
  const families = tree.filter(hasTag("FAM"));
  const familyNodes = families.map(toNode);
  let links = [].concat(...families.map(familyLinks));
  const allNodes = peopleNodes.concat(familyNodes);
  const indexedNodes = allNodes.reduce(function(memo, node, i) {
    memo[node.id] = i;
    return memo;
  }, {});
  links = links.map(idToIndex(indexedNodes));
  return {
    nodes: allNodes,
    links: links
  };
}

function getName(p) {
  if (p.tag === "INDI") {
    var nameNode = (p.tree.filter(hasTag("NAME")) || [])[0];
    if (nameNode) {
      return nameNode.data.replace(/\//g, "");
    } else {
      return "?";
    }
  } else {
    return "Family";
  }
}

function toNode(p) {
  p.id = p.pointer;
  p.name = getName(p);
  return p;
}

function idToIndex(indexedNodes) {
  return link => ({
    source: indexedNodes[link.source],
    target: indexedNodes[link.target]
  });
}

function familyLinks(family) {
  return family.tree
    .filter(member => {
      // avoid connecting MARR, etc: things that are not
      // people.
      return member.data && member.data[0] === "@";
    })
    .map(member => ({
      source: family.pointer,
      target: member.data
    }));
}

export function parse(input) {
  const start = { root: { tree: [] }, level: 0 };
  start.pointer = start.root;

  return traverse(
    input
      .split("\n")
      .map(mapLine)
      .filter(function(_) {
        return _;
      })
      .reduce(buildTree, start).root.tree
  ).map(function(node) {
    delete node.up;
    delete node.level;
    this.update(node);
  });

  // the basic trick of this module is turning the suggested tree
  // structure of a GEDCOM file into a tree in JSON. This reduction
  // does that. The only real trick is the `.up` member of objects
  // that points to a level up in the structure. This we have to
  // censor before JSON.stringify since it creates circular references.
  function buildTree(memo, data) {
    if (data.level === memo.level) {
      memo.pointer.tree.push(data);
    } else if (data.level > memo.level) {
      const up = memo.pointer;
      memo.pointer = memo.pointer.tree[memo.pointer.tree.length - 1];
      memo.pointer.tree.push(data);
      memo.pointer.up = up;
      memo.level = data.level;
    } else if (data.level < memo.level) {
      // the jump up in the stack may be by more than one, so ascend
      // until we're at the right level.
      while (data.level <= memo.pointer.level && memo.pointer.up) {
        memo.pointer = memo.pointer.up;
      }
      memo.pointer.tree.push(data);
      memo.level = data.level;
    }
    return memo;
  }

  function mapLine(data) {
    const match = data.match(lineRe);
    if (!match) return null;
    return {
      level: parseInt(match[1], 10),
      pointer: match[2].trim(),
      tag: match[3].trim(),
      data: match[4].trim(),
      tree: []
    };
  }
}
